import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import Slider from './components/Slider'
import Arrow, { DIRECTION_PREV, DIRECTION_NEXT } from './Arrow'
import ChevronLeftDefault from '../Icons/ChevronLeftDefault'
import ChevronRightDefault from '../Icons/ChevronRightDefault'

import { PROP_TYPE_CHILDREN } from '../constants'

type CarouselProps = typeof Carousel.defaultProps &
  React.PropsWithChildren<{
    autoPlay?: boolean
    autoPlaySpeed?: number
    centered?: boolean
    className?: string
    color?: string
    controls?: boolean
    dots?: boolean
    focusOnSelect?: boolean
    highlightOnActive?: boolean
    highlightOnHover?: boolean
    initialSlide?: number
    lazyLoad?: boolean
    loop?: boolean
    noVerticalSpacing?: boolean
    peek?: boolean
    scrollCount?: number
    showCount?: number
    sliderRef?: React.Ref<{
      slickNext: () => void
      slickPrev: () => void
    }> | null
    'data-testid'?: string
    transition?: string
    variableWidth?: boolean
    noBackground?: boolean
    beforeChange?: (oldIdx: number, newIdx: number) => void
    infinite?: boolean
  }>

const TRANSITION_FADE = 'fade'
const TRANSITION_SLIDE = 'slide'
const PEEK_MODIFIER = 0.25
export default class Carousel extends PureComponent<CarouselProps> {
  static propTypes = {
    autoPlay: PropTypes.bool,
    centered: PropTypes.bool,
    children: PROP_TYPE_CHILDREN,
    className: PropTypes.string,
    color: PropTypes.string,
    controls: PropTypes.bool,
    dots: PropTypes.bool,
    focusOnSelect: PropTypes.bool,
    highlightOnActive: PropTypes.bool,
    highlightOnHover: PropTypes.bool,
    initialSlide: PropTypes.number,
    loop: PropTypes.bool,
    noVerticalSpacing: PropTypes.bool,
    peek: PropTypes.bool,
    scrollCount: PropTypes.number,
    showCount: PropTypes.number,
    sliderRef: PropTypes.func,
    'data-testid': PropTypes.string,
    transition: PropTypes.string,
    variableWidth: PropTypes.bool,
    noBackground: PropTypes.bool,
  }

  static defaultProps = {
    autoPlay: false,
    autoPlaySpeed: 3000,
    centered: false,
    color: '0, 0, 0',
    controls: false,
    focusOnSelect: false,
    highlightOnActive: false,
    highlightOnHover: false,
    loop: false,
    noVerticalSpacing: false,
    scrollCount: 1,
    showCount: 3,
    transition: TRANSITION_SLIDE,
    variableWidth: false,
    noBackground: false,
  }

  arrowContainerPrevRef: React.LegacyRef<HTMLDivElement> = React.createRef()

  arrowContainerNextRef: React.LegacyRef<HTMLDivElement> = React.createRef()

  slider: React.Ref<{ slickNext: () => void; slickPrev: () => void }> | null

  handleAfterChange = (currentSlide: number) => {
    this.setState(
      {
        atBegin: false,
        atEnd: false,
      },
      () => {
        if (currentSlide === 0) {
          this.setState({ atBegin: true })
        }
        if (
          currentSlide + this.props.showCount + PEEK_MODIFIER ===
          React.Children.count(this.props.children)
        ) {
          this.setState({ atEnd: true })
        }
      },
    )
  }

  constructor(props: CarouselProps) {
    super(props)

    this.slider =
      props.sliderRef ||
      React.createRef<{ slickNext: () => void; slickPrev: () => void }>()
  }

  state = {
    initialized: false,
    atBegin: true,
    atEnd: false,
  }

  handleInit = () => this.setState({ initialized: true })

  render() {
    const {
      autoPlay,
      autoPlaySpeed,
      centered,
      children,
      className,
      controls,
      dots,
      focusOnSelect,
      highlightOnActive,
      highlightOnHover,
      loop,
      noVerticalSpacing,
      peek,
      scrollCount,
      showCount,
      transition,
      variableWidth,
      color,
      noBackground,
      lazyLoad,
      'data-testid': dataTestId,
      ...restProps
    } = this.props

    const { initialized, atBegin, atEnd } = this.state

    const topLevelClasses = cn({
      'mc-carousel': true,
      'mc-carousel--centered': centered,
      'mc-carousel--highlight-active': highlightOnActive,
      'mc-carousel--highlight-hover': highlightOnHover,
      'mc-carousel--dots': dots,
      'mc-carousel--controls': controls,
      'mc-carousel--start': atBegin,
      'mc-carousel--end': atEnd,
    })

    const containerClasses = cn({
      'mc-carousel__container': true,
      'mc-carousel__container--no-vertical-spacing': noVerticalSpacing,
    })

    const carouselClasses = cn({
      [className ?? '']: className,
      'mc-carousel__slider': true,
    })

    const showCountWithVariable = variableWidth ? 1 : showCount
    const adjustedShowCount = peek
      ? (showCountWithVariable ?? 0) + PEEK_MODIFIER
      : showCountWithVariable

    const peekStylesBefore = peek
      ? ({
          background: `linear-gradient(
            to right,
            rgba(${color}, 1) 0,
            rgba(${color}, 1) 16px,
            rgba(${color}, 0) 100%
          ) center no-repeat
        `,
        } as const)
      : {}

    const peekStylesAfter = peek
      ? ({
          background: `linear-gradient(
            to left,
            rgba(${color}, 1) 0,
            rgba(${color}, 1) 16px,
            rgba(${color}, 0) 100%
          ) center no-repeat
        `,
        } as const)
      : {}

    const arrowsProps =
      controls && initialized
        ? {
            arrows: true,
            prevArrow: (
              <Arrow
                direction={DIRECTION_PREV}
                location={
                  (
                    this
                      .arrowContainerPrevRef as React.RefObject<HTMLDivElement>
                  ).current
                }
                noBackground={noBackground}
              >
                <ChevronLeftDefault size='lg' aria-hidden='true' />
              </Arrow>
            ),
            nextArrow: (
              <Arrow
                direction={DIRECTION_NEXT}
                location={
                  (
                    this
                      .arrowContainerNextRef as React.RefObject<HTMLDivElement>
                  ).current
                }
                noBackground={noBackground}
              >
                <ChevronRightDefault size='lg' aria-hidden='true' />
              </Arrow>
            ),
          }
        : {
            arrows: false,
          }

    const dotsProps = dots
      ? {
          dots: true,
          customPaging: (i: number, active: boolean) => (
            <button
              className='slick-dot'
              type='button'
              {...(active && {
                'aria-current': true,
              })}
            >
              <span className='mc-sr-only'>{`Go to slide ${i + 1}`}</span>
            </button>
          ),
        }
      : {
          dots: false,
        }

    return (
      <div
        className={topLevelClasses}
        role='region'
        aria-label='carousel'
        {...(dataTestId ? { 'data-testid': dataTestId } : {})}
      >
        <div className='mc-carousel__forced-spacing' />
        <div className={containerClasses}>
          <div className='mc-carousel__inner-container'>
            <div
              className='mc-carousel__arrow-container'
              ref={this.arrowContainerPrevRef}
            />
            <div className='mc-carousel__slider-container'>
              <Slider
                autoplay={autoPlay}
                autoplaySpeed={autoPlaySpeed}
                className={carouselClasses}
                centerMode={centered}
                centerPadding={0}
                fade={transition === TRANSITION_FADE}
                focusOnSelect={focusOnSelect}
                ref={this.slider}
                slidesToScroll={scrollCount}
                slidesToShow={adjustedShowCount}
                infinite={loop}
                draggable={false}
                variableWidth={variableWidth}
                // progressive loading not supported on Safari
                lazyLoad={lazyLoad ? 'ondemand' : undefined}
                onInit={this.handleInit}
                afterChange={this.handleAfterChange}
                {...arrowsProps}
                {...dotsProps}
                {...restProps}
              >
                {children}
              </Slider>
            </div>
            {peek && (
              <>
                <div
                  className='mc-carousel__peek--before'
                  style={peekStylesBefore}
                />
                <div
                  className='mc-carousel__peek--after'
                  style={peekStylesAfter}
                />
              </>
            )}
            <div
              className='mc-carousel__arrow-container '
              ref={this.arrowContainerNextRef}
            />
          </div>
        </div>
        <div className='mc-carousel__forced-spacing' />
      </div>
    )
  }
}
